import { api } from '../lib/api'
import { resolveMediaUrl } from '../lib/mediaUrl'
import { clearFlow, startFlow } from '../telemetry/flow'
import { getClientRequestId } from '../telemetry/request'

export type PixePhotoRow = {
  id: string
  album_id?: string | null
  image_url: string
  thumb_url_512?: string
  caption: string
  sort_order: number
  is_public?: boolean
  is_featured?: boolean
  width?: number | null
  height?: number | null
  bytes?: number | null
  mime?: string
}

export type PixePhotoAlbumRow = {
  id: string
  title: string
  sort_order: number
  visibility: 'public' | 'subscribers' | 'ppv' | string
  price_pixe?: number
  photo_count: number
  cover_thumb_url_512?: string
  is_default?: boolean
}

export type PixePhotosPayload = {
  photos_enabled: boolean
  albums?: PixePhotoAlbumRow[]
  photos: PixePhotoRow[]
  results?: PixePhotoRow[]
  pagination?: {
    limit: number
    offset: number
    returned: number
    has_more: boolean
    next_offset?: number | null
    total_count?: number
  }
}

function normalizePhotoUrl(url?: string | null): string {
  const raw = String(url || '').trim()
  if (!raw) return ''

  const resolved = resolveMediaUrl(raw)
  if (resolved) return resolved

  // Guard against malformed internal-host payloads like "web:8082/api/..."
  // by recovering the API path and resolving it through API_BASE_URL.
  const apiIndex = raw.indexOf('/api/')
  if (apiIndex >= 0) {
    const apiPath = raw.slice(apiIndex)
    return resolveMediaUrl(apiPath) || apiPath
  }

  return raw
}

function normalizePhotoRow(row: PixePhotoRow): PixePhotoRow {
  return {
    ...row,
    image_url: normalizePhotoUrl(row.image_url),
    thumb_url_512: normalizePhotoUrl(row.thumb_url_512),
  }
}

function normalizePayload(payload: PixePhotosPayload): PixePhotosPayload {
  const albums = (payload.albums || []).map((album) => ({
    ...album,
    cover_thumb_url_512: normalizePhotoUrl(album.cover_thumb_url_512),
  }))
  const photos = (payload.photos || []).map(normalizePhotoRow)
  return {
    ...payload,
    albums,
    photos,
    // Backend mirrors photos in `results`; keep both normalized.
    results: photos,
  }
}

export async function getPixePhotos(): Promise<PixePhotosPayload> {
  const { data } = await api.get<PixePhotosPayload>(`/api/account/pixe-photos/`)
  return normalizePayload(data)
}

export type PixePhotosSettingsPayload = {
  photos_enabled: boolean
}

export async function setPixePhotosEnabled(enabled: boolean): Promise<PixePhotosSettingsPayload> {
  const { data } = await api.patch<PixePhotosSettingsPayload>(`/api/account/pixe-photos/settings/`, {
    photos_enabled: enabled,
  })
  return data
}

export async function registerPixePhoto(args: {
  storage_key: string
  thumb_key_512?: string
  caption?: string
  sort_order?: number
  is_public?: boolean
  album_id?: string | null
  width?: number | null
  height?: number | null
  bytes?: number | null
  mime?: string
}): Promise<PixePhotoRow> {
  const flow = startFlow('photos_upload')
  const headers = {
    'X-Client-Action': 'photos_upload',
    'X-Client-Flow-Id': flow.flowId,
    'X-Client-Request-Id': getClientRequestId(),
  }
  try {
    const { data } = await api.post<PixePhotoRow>(`/api/account/pixe-photos/`, args, { headers })
    return normalizePhotoRow(data)
  } finally {
    clearFlow(flow.flowId)
  }
}

export async function deletePixePhoto(id: string): Promise<void> {
  await api.delete(`/api/account/pixe-photos/${encodeURIComponent(id)}/`)
}

export async function patchPixePhoto(
  id: string,
  patch: { caption?: string; sort_order?: number; is_featured?: boolean; album_id?: string | null }
): Promise<PixePhotoRow> {
  const { data } = await api.patch<PixePhotoRow>(`/api/account/pixe-photos/${encodeURIComponent(id)}/`, patch)
  return normalizePhotoRow(data)
}

export async function reorderPixePhotos(orderedIds: string[], albumId?: string | null): Promise<PixePhotosPayload> {
  const { data } = await api.patch<PixePhotosPayload>(`/api/account/pixe-photos/reorder/`, {
    ordered_ids: orderedIds,
    album_id: albumId ?? null,
  })
  return normalizePayload(data)
}

export async function createPixePhotoAlbum(args: { title: string; visibility?: 'public' | 'subscribers' | 'ppv' }): Promise<PixePhotoAlbumRow> {
  const { data } = await api.post<PixePhotoAlbumRow>(`/api/account/pixe-photo-albums/`, args)
  return data
}

export async function patchPixePhotoAlbum(
  id: string,
  patch: { title?: string; visibility?: 'public' | 'subscribers' | 'ppv'; sort_order?: number; price_pixe?: number }
): Promise<PixePhotoAlbumRow> {
  const { data } = await api.patch<PixePhotoAlbumRow>(`/api/account/pixe-photo-albums/${encodeURIComponent(id)}/`, patch)
  return data
}

export async function deletePixePhotoAlbum(id: string): Promise<void> {
  await api.delete(`/api/account/pixe-photo-albums/${encodeURIComponent(id)}/`)
}

export async function reorderPixePhotoAlbums(orderedIds: string[]): Promise<PixePhotosPayload> {
  const { data } = await api.patch<PixePhotosPayload>(`/api/account/pixe-photo-albums/reorder/`, { ordered_ids: orderedIds })
  return normalizePayload(data)
}

export async function unlockPixePhotoAlbum(albumId: string, idempotencyKey?: string): Promise<{ unlocked: boolean; already_unlocked: boolean }> {
  const { data } = await api.post(
    `/api/pixe-photos/albums/${encodeURIComponent(albumId)}/unlock/`,
    {},
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined
  )
  return data
}
